import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { Button } from "@components/ui/Button";

import { useLocale } from "@lib/contexts/LocaleContext";
import { LanguageLabels, LegalLinks, UtilityLinks } from "@root/constants.ts";

import { siteLink } from "@/helpers";

const PageFooter: React.FC<{
  bgColor?: string;
  textColor?: string;
  /**
   * When set, the language switcher uses the marketing locale set
   * (VITE_ENABLED_MARKETING_LANGS — matches what /join surfaces via
   * hreflang) instead of the full technique-pages locale set. Used by
   * GiftPageFooter so the gift give / redeem / printable-card pages
   * only offer locales we actually support marketing in.
   */
  useMarketingLangs?: boolean;
}> = ({ bgColor, textColor, useMarketingLangs = false }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { locale, changeLocale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const linkClassName = clsx(
    textColor ? textColor : "text-ui-coal",
    "hover:underline",
  );

  // Remove the existing 'lng' query parameter so we don't end up with more than one in URL
  searchParams.delete("lng");

  // Convert searchParams to string, preserving existing query parameters
  const queryString = searchParams.toString();
  const queryParamString = queryString ? `&${queryString}` : "";

  const enabledLanguagesEnv = useMarketingLangs
    ? import.meta.env.VITE_ENABLED_MARKETING_LANGS
    : import.meta.env.VITE_ENABLED_TECHNIQUE_LANGS;
  const enabledLanguages = (enabledLanguagesEnv?.split(",") || []).map(
    (lang: string) => lang.toLowerCase(),
  );

  const languageLinks = Object.entries(LanguageLabels)
    .filter(([key]) => enabledLanguages.includes(key.toLowerCase()))
    .map(([key, label]) => ({
      label,
      url: `/${key.toLowerCase()}${queryParamString}`,
      locale: key,
      active: key === locale,
    }));

  const handleLanguageClick = (locale: string) => {
    changeLocale(locale);

    if (locale !== "en" && location.pathname === "/members/home") {
      return navigate(`/collection/essentials`);
    }
  };

  return (
    <footer
      className={clsx("mt-auto backdrop-blur-none lg:relative lg:z-[200]")}
    >
      <div
        className={clsx(
          "w-full p-32px text-body-4",
          bgColor ?? "bg-ui-tbd2",
          textColor ?? "text-ui-coal",
        )}
      >
        <div
          className={clsx(
            "flex flex-wrap justify-center",
            "space-x-16px gap-y-16px",
            "px-0 md:px-32px lg:px-0",
          )}
        >
          {languageLinks.map(({ label, active, locale }, index) => (
            <Button
              variant="link"
              size="link"
              className={clsx(
                textColor || (active ? "text-ui-pepper" : "text-ui-coal"),
                active ? "underline" : "hover:underline",
                "!px-0",
              )}
              onClick={() => handleLanguageClick(locale)}
              key={index}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div
        className={clsx(
          "w-full",
          "section-p-x !py-20px",
          "!text-ui-coal text-body-4",
          bgColor ?? "bg-ui-sea-salt",
          "space-y-4px",
        )}
      >
        <div
          className={clsx(
            "flex flex-wrap justify-center pb-4px text-center",
            "[&>*]:px-8px sm:[&>*]:px-12px [&>*]:pb-16px",
          )}
        >
          <a
            className={linkClassName}
            href={`${import.meta.env.VITE_OMGYES_SERVER_URL}/gifts`}
          >
            {t("label.gifts")}
          </a>

          {[...UtilityLinks, ...LegalLinks].map(({ label, url }, index) => {
            return (
              <a className={linkClassName} href={siteLink(url)} key={index}>
                {t(label)}
              </a>
            );
          })}
        </div>
        <div className={clsx("text-body-4 text-center")}>
          <p
            className={clsx(
              "text-body-4 opacity-25",
              textColor ?? "!text-ui-coal",
            )}
          >
            &copy;{t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default PageFooter;
